import React, { useState, useEffect, useCallback } from 'react';
import { 
  XMarkIcon, 
  EyeIcon, 
  CodeBracketIcon,
  DocumentDuplicateIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import templateService, { EmailTemplate, TemplatePreview as TemplatePreviewData } from '../../services/templateService';

interface TemplatePreviewProps {
  template: EmailTemplate;
  onClose: () => void;
  onEdit: (template: EmailTemplate) => void;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, onClose, onEdit }) => {
  const [preview, setPreview] = useState<TemplatePreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'preview' | 'html' | 'text'>('preview');
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [testingGeneration, setTestingGeneration] = useState(false);

  const loadPreview = useCallback(async () => {
    try {
      setLoading(true);
      const result = await templateService.previewTemplate(template.id, customVariables);
      setPreview(result.preview);
      setError(null);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [template.id, customVariables]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleVariableChange = (key: string, value: string) => {
    setCustomVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTestGeneration = async () => {
    try {
      setTestingGeneration(true);
      await templateService.testTemplateGeneration(template.id);
      alert('Template generation test completed successfully! Check the console for details.');
    } catch (err) {
      console.error('Template generation test failed:', err);
      alert(err instanceof Error ? err.message : 'Template generation test failed');
    } finally {
      setTestingGeneration(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      newsletter: 'bg-blue-100 text-blue-800',
      welcome: 'bg-green-100 text-green-800',
      resources: 'bg-purple-100 text-purple-800',
      events: 'bg-orange-100 text-orange-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  const getDefaultVariableValue = (key: string) => {
    const defaults: Record<string, string> = {
      school_name: 'Sunshine Academy',
      newsletter_title: 'Weekly Update',
      parent_name: 'Sarah Johnson',
      child_name: 'Emma',
      child_age: '8',
      timeline: 'Next semester',
      special_needs: 'Autism spectrum support',
      main_content: '<p>This is sample content for preview purposes. Your AI-generated content will appear here.</p><ul><li>Important update #1</li><li>Exciting news to share</li><li>Reminder about upcoming events</li></ul>',
      next_steps_content: '<ul><li>Complete enrollment forms</li><li>Schedule intake meeting</li><li>Submit medical records</li></ul>',
      resource_title: 'Special Needs Learning Toolkit',
      resource_description: '<p>A comprehensive collection of learning tools and resources specifically designed for children with special needs.</p>',
      resource_type: 'Learning Resources',
      target_audience: '<ul><li>Children with autism spectrum disorders</li><li>Students with ADHD</li><li>Families seeking specialized support</li></ul>',
      cta_text: 'Schedule a Meeting',
      cta_url: 'https://example.com/schedule',
      download_cta_text: 'Download Resource',
      info_cta_text: 'Learn More',
      download_url: 'https://example.com/download',
      info_url: 'https://example.com/info',
      contact_email: 'contact@sunshineacademy.edu',
      contact_phone: '(555) 123-4567'
    };

    return customVariables[key] || defaults[key] || `{{${key}}}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            <span>Loading preview...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
              <div className="flex items-center space-x-3 mt-1">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(template.category)}`}>
                  {template.category}
                </span>
                <span className="text-sm text-gray-500">
                  Used {template.usage_count} times
                </span>
                <span className="text-sm text-gray-500">
                  Last used: {formatDate(template.last_used_at)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleTestGeneration}
              disabled={testingGeneration}
              className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm flex items-center disabled:opacity-50"
            >
              {testingGeneration ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Test AI Generation
                </>
              )}
            </button>
            <button
              onClick={() => onEdit(template)}
              className="bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 text-sm"
            >
              Edit Template
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(95vh-140px)]">
          {/* Left Panel - Template Info & Variables */}
          <div className="lg:w-1/3 border-r border-gray-200 overflow-y-auto">
            <div className="p-6">
              {/* Template Info */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Template Information</h4>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Description:</span>
                    <p className="text-sm text-gray-700 mt-1">
                      {template.description || 'No description provided'}
                    </p>
                  </div>
                  
                  {template.segment_tags && template.segment_tags.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Segment Tags:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template.segment_tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="text-sm font-medium text-gray-500">Created:</span>
                    <p className="text-sm text-gray-700">{formatDate(template.created_at)}</p>
                  </div>

                  {template.created_by && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Created by:</span>
                      <p className="text-sm text-gray-700">{template.created_by}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Variables */}
              {template.variables_schema && template.variables_schema.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Custom Preview Values</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Customize these values to see how your template looks with different content.
                  </p>
                  
                  <div className="space-y-3">
                    {template.variables_schema.map((variable, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {variable.label}
                          {variable.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {variable.type === 'html' ? (
                          <textarea
                            value={customVariables[variable.key] || getDefaultVariableValue(variable.key)}
                            onChange={(e) => handleVariableChange(variable.key, e.target.value)}
                            rows={3}
                            className="w-full text-sm border-gray-300 rounded-md focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder={variable.default_value || `Enter ${variable.label.toLowerCase()}`}
                          />
                        ) : (
                          <input
                            type={variable.type === 'url' ? 'url' : 'text'}
                            value={customVariables[variable.key] || getDefaultVariableValue(variable.key)}
                            onChange={(e) => handleVariableChange(variable.key, e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder={variable.default_value || `Enter ${variable.label.toLowerCase()}`}
                          />
                        )}
                        {variable.description && (
                          <p className="text-xs text-gray-500 mt-1">{variable.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Reset */}
              <button
                onClick={() => setCustomVariables({})}
                className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm hover:bg-gray-200"
              >
                Reset to Defaults
              </button>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="sticky top-0 bg-gray-50 p-4 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Email Preview</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('preview')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      viewMode === 'preview'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    <EyeIcon className="h-4 w-4 inline mr-1" />
                    Preview
                  </button>
                  <button
                    onClick={() => setViewMode('html')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      viewMode === 'html'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    <CodeBracketIcon className="h-4 w-4 inline mr-1" />
                    HTML
                  </button>
                  <button
                    onClick={() => setViewMode('text')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      viewMode === 'text'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 inline mr-1" />
                    Text
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {error ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-700">{error}</p>
                </div>
              ) : preview ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Email Header Simulation */}
                  <div className="border-b border-gray-200 p-4 bg-gray-50 rounded-t-lg">
                    <div className="text-sm text-gray-500 mb-2">Subject Line:</div>
                    <div className="text-lg font-medium text-gray-900">
                      {preview.subject}
                    </div>
                  </div>

                  {/* Email Content */}
                  <div className="p-6">
                    {viewMode === 'preview' && (
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: preview.html_content }}
                        style={{
                          fontFamily: 'Arial, sans-serif',
                          lineHeight: '1.6',
                          color: '#333'
                        }}
                      />
                    )}

                    {viewMode === 'html' && (
                      <pre className="bg-gray-900 text-gray-300 p-4 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
                        {preview.html_content}
                      </pre>
                    )}

                    {viewMode === 'text' && (
                      <pre className="bg-gray-100 p-4 rounded-md text-sm whitespace-pre-wrap font-mono">
                        {preview.text_content}
                      </pre>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <div className="text-gray-400 text-lg mb-2">📧</div>
                  <div className="text-gray-500 text-sm">
                    Preview loading...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Preview generated with sample data • Variables: {template.variables_schema?.length || 0}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => onEdit(template)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Edit Template
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;